/**
 * Approvals bell — the pending-decision queue, in the top bar.
 *
 * UI_CONSOLIDATION_PLAN 4B. Approvals leaves the nav and becomes a bell,
 * because it is not a place you go, it is a thing that happens to you: a staff
 * member raises a request and the owner has to say yes. A nav item for that sat
 * at the bottom of the Account group reading "Approvals (0)" every day of the
 * year, and got checked on the days it mattered least.
 *
 * 🔴 THIS ADDS NO ENDPOINT AND NO PAGE. It reads `GET /api/approvals/?status=
 * pending` — the same call `pages/Approvals` makes — and every row links into
 * `/approvals`, which is untouched and still the full list. Deciding is done
 * there: a two-button popover would need the whole permission story
 * (`CanAccessSettings`, the 409-on-already-decided path) reproduced in a
 * dropdown, and a half-copy of that is how one gets decided twice.
 *
 * Gated on the SAME capability as the nav item it replaces (`approvals`), so a
 * business that never switched it on has no bell at all — not an empty one.
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, ShieldCheck } from 'lucide-react';

import AuthContext from '../../context/AuthContext';
import API_BASE_URL from '../../config/api';
import { devLog } from '../../utils/devLog';
import {
  hasCapability, configVersion, subscribeBusinessConfig,
} from '../../utils/businessConfig';

// Slow on purpose. An approval is a human waiting on another human, not a
// ticker — a minute of latency costs nothing and a 5s poll on every open tab
// costs a request per second per merchant.
const POLL_MS = 60000;

const KIND_LABELS = {
  purchase: 'Purchase', discount: 'Discount', refund: 'Refund', other: 'Request',
};

const fmtAmount = (r) => {
  const n = Number(r.amount) || 0;
  return r.kind === 'discount' ? `${n}%` : `₹${n.toLocaleString('en-IN')}`;
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const ApprovalsBell = () => {
  const { authToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const wrapRef = useRef(null);

  // The capability lives in a module-level config that arrives after login, so
  // subscribe rather than reading once — otherwise the bell is missing until
  // the next full navigation.
  const [cfgVersion, setCfgVersion] = useState(configVersion);
  useEffect(() => {
    if (configVersion() !== cfgVersion) setCfgVersion(configVersion());
    const unsubscribe = subscribeBusinessConfig(setCfgVersion);
    const onToolsChanged = () => setCfgVersion(configVersion());
    window.addEventListener('inikru:tools-changed', onToolsChanged);
    return () => {
      unsubscribe();
      window.removeEventListener('inikru:tools-changed', onToolsChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enabled = hasCapability('approvals');

  const authAxios = useMemo(
    () => axios.create({
      baseURL: `${API_BASE_URL}/api`,
      headers: { Authorization: `Bearer ${authToken?.access}` },
    }),
    [authToken?.access]
  );

  const load = useCallback(async () => {
    if (!enabled || !authToken?.access) return;
    try {
      const { data } = await authAxios.get('/approvals/?status=pending');
      setRows(Array.isArray(data?.requests) ? data.requests : []);
    } catch (err) {
      // A bell that throws would take the whole top bar with it.
      devLog.warn('approvals bell skipped:', err?.response?.status);
    }
  }, [authAxios, authToken?.access, enabled]);

  useEffect(() => {
    load();
    if (!enabled) return undefined;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load, enabled]);

  // Click-away + Esc, so the popover behaves like every other popover.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!enabled) return null;

  const count = rows.length;
  const go = () => { setOpen(false); navigate('/approvals'); };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        aria-label={count ? `${count} approvals waiting` : 'Approvals'}
        aria-expanded={open}
        className="relative grid h-9 w-9 place-items-center rounded-full border border-line bg-white text-ink transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <Bell className="h-4 w-4" />
        {count > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 z-50 mt-2 w-[320px] overflow-hidden rounded-xl border border-line bg-white shadow-[0_24px_60px_-30px_rgba(22,19,15,0.55)]"
          role="dialog"
          aria-label="Pending approvals"
        >
          <div className="flex items-center gap-2 border-b border-line px-4 py-3">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <span className="font-display text-[12px] font-bold uppercase tracking-[0.14em] text-gray-500">
              Waiting on you
            </span>
          </div>

          {count === 0 ? (
            <p className="px-4 py-6 text-center text-[13px] text-muted">
              Nothing waiting for a decision.
            </p>
          ) : (
            <ul className="max-h-[320px] divide-y divide-line overflow-y-auto">
              {rows.slice(0, 8).map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={go}
                    className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block font-display text-[13.5px] font-bold text-ink">
                        {KIND_LABELS[r.kind] || 'Request'} · {fmtAmount(r)}
                      </span>
                      <span className="block truncate text-[12px] text-muted">
                        {r.description || `Raised by ${r.requested_by_name || 'a team member'}`}
                      </span>
                    </span>
                    <span className="shrink-0 text-[11px] text-muted">{fmtWhen(r.created_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={go}
            className="w-full border-t border-line px-4 py-3 text-[12.5px] font-bold text-primary transition-colors hover:bg-gray-50"
          >
            {count > 8 ? `See all ${count}` : 'Open Approvals'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ApprovalsBell;
